// Types and declarative requirement definitions for the project image-rules API.
// The mapper in `imageRequirementsMapper.ts` turns the backend config into
// UI-friendly requirement items using the definitions below.

export interface IImageRulesConfig {
  enabled?: boolean;
  failFast?: boolean;
  validationMode?: string;
  faceRequired?: boolean;
  singleFaceOnly?: boolean;
  faceFullyVisible?: boolean;
  eyesOpen?: boolean;
  minFaceCoveragePercent?: number;
  maxFaceTiltDegrees?: number;
  faceConfidenceThreshold?: number;
  disallowSunglasses?: boolean;
  sunglassesConfidenceThreshold?: number;
  disallowOcclusion?: boolean;
  disallowCaps?: boolean;
  capConfidenceThreshold?: number;
  disallowMasks?: boolean;
  maskConfidenceThreshold?: number;
  checkBlur?: boolean;
  blurThreshold?: number;
  checkBrightness?: boolean;
  brightnessMin?: number;
  brightnessMax?: number;
  checkContrast?: boolean;
  minWidthPx?: number;
  minHeightPx?: number;
  allowJpeg?: boolean;
  allowPng?: boolean;
  maxFileSizeMb?: number;
  requireBackgroundColor?: boolean;
  backgroundColor?: string;
  backgroundColorTolerance?: number;
  minBackgroundMatchPercent?: number;
  duplicateDetectionEnabled?: boolean;
  duplicateSimilarityThreshold?: number;
}

export type RequirementIconId =
  | 'single-face'
  | 'face-visible'
  | 'eyes-open'
  | 'no-sunglasses'
  | 'no-caps'
  | 'no-masks'
  | 'no-occlusion'
  | 'background'
  | 'sharp-photo'
  | 'lighting'
  | 'contrast'
  | 'jpeg'
  | 'png'
  | 'file-size';

export interface IRequirementDefinition {
  id: string;
  iconId: RequirementIconId;
  titleKey: string;
  titleDefault: string;
  descriptionKey: string;
  descriptionDefault: string;
  isEnabled: (config: IImageRulesConfig) => boolean;
  getBackgroundColor?: (config: IImageRulesConfig) => string | null;
  getDescriptionValues?: (
    config: IImageRulesConfig,
  ) => Record<string, string | number> | undefined;
}

export const REQUIREMENT_DEFINITIONS: IRequirementDefinition[] = [
  {
    id: 'single-face',
    iconId: 'single-face',
    titleKey: 'PHOTO_REQ_ONE_PERSON_TITLE',
    titleDefault: 'One person only',
    descriptionKey: 'PHOTO_REQ_ONE_PERSON_DESC',
    descriptionDefault: 'Your photo must contain exactly one person.',
    isEnabled: (config) =>
      !!config.faceRequired || !!config.singleFaceOnly,
  },
  {
    id: 'face-visible',
    iconId: 'face-visible',
    titleKey: 'PHOTO_REQ_FACE_VISIBLE_TITLE',
    titleDefault: 'Face fully visible',
    descriptionKey: 'PHOTO_REQ_FACE_VISIBLE_DESC',
    descriptionDefault: 'Your entire face should be visible.',
    isEnabled: (config) => !!config.faceFullyVisible,
  },
  {
    id: 'eyes-open',
    iconId: 'eyes-open',
    titleKey: 'PHOTO_REQ_EYES_OPEN_TITLE',
    titleDefault: 'Eyes open',
    descriptionKey: 'PHOTO_REQ_EYES_OPEN_DESC',
    descriptionDefault: 'Look directly at the camera with both eyes open.',
    isEnabled: (config) => !!config.eyesOpen,
  },
  {
    id: 'no-sunglasses',
    iconId: 'no-sunglasses',
    titleKey: 'PHOTO_REQ_NO_SUNGLASSES_TITLE',
    titleDefault: 'No sunglasses',
    descriptionKey: 'PHOTO_REQ_NO_SUNGLASSES_DESC',
    descriptionDefault: 'Remove sunglasses before taking the photo.',
    isEnabled: (config) => !!config.disallowSunglasses,
  },
  {
    id: 'no-caps',
    iconId: 'no-caps',
    titleKey: 'PHOTO_REQ_NO_CAPS_TITLE',
    titleDefault: 'No hats or caps',
    descriptionKey: 'PHOTO_REQ_NO_CAPS_DESC',
    descriptionDefault:
      'Do not wear hats, caps or head coverings unless permitted.',
    isEnabled: (config) => !!config.disallowCaps,
  },
  {
    id: 'no-masks',
    iconId: 'no-masks',
    titleKey: 'PHOTO_REQ_NO_MASKS_TITLE',
    titleDefault: 'No face masks',
    descriptionKey: 'PHOTO_REQ_NO_MASKS_DESC',
    descriptionDefault: 'Your face must not be covered.',
    isEnabled: (config) => !!config.disallowMasks,
  },
  {
    id: 'no-occlusion',
    iconId: 'no-occlusion',
    titleKey: 'PHOTO_REQ_NO_OCCLUSION_TITLE',
    titleDefault: 'No face obstruction',
    descriptionKey: 'PHOTO_REQ_NO_OCCLUSION_DESC',
    descriptionDefault:
      'Hair, hands or other objects should not cover your face.',
    isEnabled: (config) => !!config.disallowOcclusion,
  },
  {
    id: 'background',
    iconId: 'background',
    titleKey: 'PHOTO_REQ_BACKGROUND_TITLE',
    titleDefault: 'Plain background',
    descriptionKey: 'PHOTO_REQ_BACKGROUND_DESC',
    descriptionDefault:
      'Use a plain background matching the required color.',
    isEnabled: (config) => !!config.requireBackgroundColor,
    getBackgroundColor: (config) => config.backgroundColor ?? null,
  },
  {
    id: 'sharp-photo',
    iconId: 'sharp-photo',
    titleKey: 'PHOTO_REQ_SHARP_PHOTO_TITLE',
    titleDefault: 'Sharp photo',
    descriptionKey: 'PHOTO_REQ_SHARP_PHOTO_DESC',
    descriptionDefault: 'The photo should not be blurry.',
    isEnabled: (config) => !!config.checkBlur,
  },
  {
    id: 'lighting',
    iconId: 'lighting',
    titleKey: 'PHOTO_REQ_LIGHTING_TITLE',
    titleDefault: 'Good lighting',
    descriptionKey: 'PHOTO_REQ_LIGHTING_DESC',
    descriptionDefault: 'Ensure the photo is well lit.',
    isEnabled: (config) => !!config.checkBrightness,
  },
  {
    id: 'contrast',
    iconId: 'contrast',
    titleKey: 'PHOTO_REQ_CONTRAST_TITLE',
    titleDefault: 'Good contrast',
    descriptionKey: 'PHOTO_REQ_CONTRAST_DESC',
    descriptionDefault: 'Avoid very dark or washed-out photos.',
    isEnabled: (config) => !!config.checkContrast,
  },
  {
    id: 'jpeg',
    iconId: 'jpeg',
    titleKey: 'PHOTO_REQ_JPEG_TITLE',
    titleDefault: 'JPG supported',
    descriptionKey: 'PHOTO_REQ_JPEG_DESC',
    descriptionDefault: 'JPG format is supported.',
    isEnabled: (config) => !!config.allowJpeg,
  },
  {
    id: 'png',
    iconId: 'png',
    titleKey: 'PHOTO_REQ_PNG_TITLE',
    titleDefault: 'PNG supported',
    descriptionKey: 'PHOTO_REQ_PNG_DESC',
    descriptionDefault: 'PNG format is supported.',
    isEnabled: (config) => !!config.allowPng,
  },
  {
    id: 'max-file-size',
    iconId: 'file-size',
    titleKey: 'PHOTO_REQ_MAX_FILE_SIZE_TITLE',
    titleDefault: 'Maximum file size',
    descriptionKey: 'PHOTO_REQ_MAX_FILE_SIZE_DESC',
    descriptionDefault: '{{size}} MB',
    isEnabled: (config) => config.maxFileSizeMb != null,
    getDescriptionValues: (config) => ({
      size: config.maxFileSizeMb as number,
    }),
  },
];

export interface IRequirementItem {
  id: string;
  iconId: RequirementIconId;
  titleKey: string;
  titleDefault: string;
  descriptionKey: string;
  descriptionDefault: string;
  descriptionValues?: Record<string, string | number>;
  backgroundColor?: string | null;
}
